import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { 
  Package, 
  ScanBarcode, 
  ClipboardList, 
  Truck, 
  Settings, 
  Users, 
  UserPlus,
  Bell, 
  Cloud,
  CloudCog,
  Database,
  LogOut,
  PanelLeftClose,
  PanelLeft
} from "lucide-react";
import { cn } from "../lib/utils";

const SIDEBAR_STORAGE_KEY = "sidebar_collapsed";

const navItems = [
  { 
    path: "/items", 
    label: "Items", 
    icon: Package,
    roles: ["admin", "owner", "purchasing", "receiving", "scanner", "viewer"]
  },
  { 
    path: "/scan", 
    label: "Scan Station", 
    icon: ScanBarcode,
    roles: ["admin", "owner", "scanner", "viewer"]
  },
  { 
    path: "/orders", 
    label: "Order Queue", 
    icon: ClipboardList,
    roles: ["admin", "owner", "purchasing", "viewer"]
  },
  { 
    path: "/receiving", 
    label: "Receiving", 
    icon: Truck,
    roles: ["admin", "owner", "receiving", "viewer"]
  },
];

const adminItems = [
  { path: "/admin/users", label: "Users", icon: Users },
  { path: "/admin/invitations", label: "Invitations", icon: UserPlus },
  { path: "/admin/notifications", label: "Notifications", icon: Bell },
  { path: "/admin/google", label: "Google Workspace", icon: Cloud },
  { path: "/admin/salesforce", label: "Salesforce", icon: CloudCog },
  { path: "/admin/master-data", label: "Master Data", icon: Database },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

// NavLink component with tooltip support for collapsed mode
function NavLink({ item, isActive, isCollapsed }) {
  const Icon = item.icon;
  
  // Debug logging (remove after fix)
  const handleClick = (e) => {
    console.log('[NavLink] Clicked:', {
      label: item.label,
      path: item.path,
      isActive
    });
  };
  
  const linkContent = (
    <Link
      to={item.path}
      onClick={handleClick}
      data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
      className={cn(
        "flex items-center gap-3 rounded-sm text-sm font-medium transition-all duration-200",
        isCollapsed ? "px-3 py-2.5 justify-center" : "px-3 py-2.5",
        isActive 
          ? "bg-primary/10 text-primary border border-primary/30" 
          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
      )}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span 
        className={cn(
          "uppercase tracking-wide whitespace-nowrap transition-all duration-200",
          isCollapsed ? "w-0 opacity-0 overflow-hidden" : "opacity-100"
        )}
      >
        {item.label}
      </span>
    </Link>
  );
  
  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {linkContent}
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }
  
  return linkContent;
}

export default function MainLayout() {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize collapsed state from localStorage (synchronously to avoid flash)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    }
    return false;
  });
  
  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);
  
  // Toggle sidebar collapsed state
  const toggleSidebar = () => {
    setIsCollapsed(prev => !prev);
  };
  
  // Handle logout - clear state and navigate to login
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };
  
  // Don't render anything if not authenticated (AuthGuard should prevent this)
  if (!isAuthenticated || !user) {
    return null;
  }
  
  // User has roles array, check if they have any matching role
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const filteredNavItems = navItems.filter(item => 
    item.roles.some(role => userRoles.includes(role))
  );
  
  // Safety: If no roles match, show core items to prevent blank sidebar
  const displayNavItems = filteredNavItems.length > 0 
    ? filteredNavItems 
    : [navItems[0]]; // At least show Items
  
  // Check if user is admin (has 'admin' or 'owner' role)
  const isAdmin = userRoles.includes('admin') || userRoles.includes('owner');

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <aside 
          className={cn(
            "border-r border-border bg-card flex flex-col h-screen sticky top-0 transition-all duration-200 ease-in-out",
            isCollapsed ? "w-[72px]" : "w-64"
          )}
        >
          {/* Top: Logo + Collapse Toggle */}
          <div className={cn(
            "h-16 border-b border-border flex items-center shrink-0 transition-all duration-200",
            isCollapsed ? "px-2 justify-center" : "px-4 gap-3"
          )}>
            <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div className={cn(
              "transition-all duration-200 overflow-hidden",
              isCollapsed ? "w-0 opacity-0" : "opacity-100"
            )}>
              <h1 className="font-heading text-lg tracking-tight whitespace-nowrap">KANBAN</h1>
              <p className="text-xs text-muted-foreground -mt-1 whitespace-nowrap">INVENTORY</p>
            </div>
          </div>
          
          {/* Middle: Scrollable Navigation */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
            <nav className={cn(
              "space-y-1 transition-all duration-200",
              isCollapsed ? "px-2" : "px-2"
            )}>
              {displayNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink 
                    key={item.path} 
                    item={item} 
                    isActive={isActive} 
                    isCollapsed={isCollapsed} 
                  />
                );
              })}
              
              {/* Admin Section */}
              {isAdmin && (
                <>
                  <div className={cn(
                    "pt-4 pb-2 transition-all duration-200",
                    isCollapsed ? "px-0" : "px-3"
                  )}>
                    {isCollapsed ? (
                      <div className="border-t border-border mx-2" />
                    ) : (
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Admin
                      </span>
                    )}
                  </div>
                  {adminItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink 
                        key={item.path} 
                        item={item} 
                        isActive={isActive} 
                        isCollapsed={isCollapsed} 
                      />
                    );
                  })}
                </>
              )}
            </nav>
          </div>
          
          {/* Bottom: User Info + Collapse Toggle (Always Visible) */}
          <div className="border-t border-border shrink-0">
            {/* Collapse Toggle */}
            <div className={cn(
              "border-b border-border/50 transition-all duration-200",
              isCollapsed ? "p-2" : "px-4 py-2"
            )}>
              {isCollapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleSidebar}
                      className="w-full h-9"
                      data-testid="expand-sidebar-btn"
                    >
                      <PanelLeft className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Expand sidebar
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
                  data-testid="collapse-sidebar-btn"
                >
                  <PanelLeftClose className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">Collapse</span>
                </Button>
              )}
            </div>
            
            {/* User Info */}
            <div className={cn(
              "transition-all duration-200",
              isCollapsed ? "p-2" : "p-4"
            )}>
              {isCollapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleLogout}
                      className="w-full h-10"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div>
                      <p className="font-medium">{user.name || user.email}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                      <p className="text-xs text-muted-foreground mt-1">Click to logout</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      {userRoles.join(', ')}
                    </p>
                  </div>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleLogout}
                        className="shrink-0"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Logout
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-screen overflow-auto">
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
}
